import { Link, useParams } from 'react-router-dom';
import { useEffect } from 'react';
import { ArrowRight, MapPin, Clock, TrendingUp, Users, Check } from 'lucide-react';
import PageHeader from '../components/PageHeader';
import ServiceIcon from '../components/ServiceIcon';
import { MARKETS, SERVICES, INDUSTRIES } from '../mock';
import { CITIES } from '../data/cities';
import useSEO from '../hooks/useSEO';

const MARKET_COPY = {
  canada: {
    intro: 'Ransen is a full-service digital marketing agency helping Canadian brands scale nationally and internationally. From Toronto to Vancouver, we combine paid media, SEO, LLM optimization, and AI automation to grow ambitious businesses.',
    focus: ['Bilingual EN/FR campaigns', 'CASL & PIPEDA compliant tracking', 'Cross-border US expansion', 'Provincial GEO strategies'],
    stats: [
      { icon: MapPin, label: 'Cities Served', value: '5+' },
      { icon: Clock, label: 'Local Team Hours', value: 'ET/PT' },
      { icon: TrendingUp, label: 'Avg. Growth', value: '3.4x' },
      { icon: Users, label: 'Canadian Clients', value: '80+' },
    ],
  },
  'united-states': {
    intro: 'Ransen is a leading digital marketing agency for United States brands, helping companies scale across the world\u2019s largest and most competitive market. Coast-to-coast performance marketing programs backed by senior operators.',
    focus: ['State-level compliance (CCPA, HIPAA)', 'Coast-to-coast time zone coverage', 'Enterprise martech integrations', 'Multi-state PPC + local SEO'],
    stats: [
      { icon: MapPin, label: 'States Covered', value: '48' },
      { icon: Clock, label: 'Time Zones', value: 'All 4' },
      { icon: TrendingUp, label: 'ROAS Lift', value: '+42%' },
      { icon: Users, label: 'US Clients', value: '50+' },
    ],
  },
  'united-kingdom': {
    intro: 'Ransen is a UK digital marketing agency delivering GDPR-native performance programs across London, Manchester, Birmingham, and beyond. We serve UK fintech, ecommerce, and B2B SaaS leaders with senior strategists on GMT.',
    focus: ['GDPR + PECR compliance', 'Consent Mode v2 implementation', 'UK + EU expansion playbooks', 'London-based reporting hours'],
    stats: [
      { icon: MapPin, label: 'UK Cities', value: '9' },
      { icon: Clock, label: 'Reporting', value: 'GMT' },
      { icon: TrendingUp, label: 'Median CAC drop', value: '\u221228%' },
      { icon: Users, label: 'UK Clients', value: '20+' },
    ],
  },
  australia: {
    intro: 'Ransen is Australia\u2019s AI-powered digital marketing agency serving Sydney, Melbourne, Brisbane, and Perth. Full-funnel performance marketing, SEO, and automation on AEDT/AEST with APAC expansion capability.',
    focus: ['APAC-aligned support hours', 'Multi-state DTC + retail', 'AU Privacy Act aligned tracking', 'Cross-border NZ expansion'],
    stats: [
      { icon: MapPin, label: 'AU Cities', value: '5' },
      { icon: Clock, label: 'Time Zone', value: 'AEDT' },
      { icon: TrendingUp, label: 'Avg. ROAS', value: '5.1x' },
      { icon: Users, label: 'AU Clients', value: '15+' },
    ],
  },
  uae: {
    intro: 'Ransen is a Dubai-headquartered digital marketing agency serving the UAE and wider GCC. Bilingual Arabic-English creative, luxury retail programs, and Ramadan-aware campaigns for UAE\u2019s premium brands.',
    focus: ['Arabic + English creative', 'Ramadan seasonality planning', 'GCC-wide expansion', 'Dubai time zone support'],
    stats: [
      { icon: MapPin, label: 'GCC Cities', value: '7' },
      { icon: Clock, label: 'Reporting', value: 'GST' },
      { icon: TrendingUp, label: 'Avg. Lift', value: '3.6x' },
      { icon: Users, label: 'MENA Clients', value: '12+' },
    ],
  },
  singapore: {
    intro: 'Ransen is Singapore\u2019s APAC marketing agency \u2014 serving Southeast Asia\u2019s largest fintech and SaaS ecosystem with PDPA-compliant, multi-language programs and regional expansion capability.',
    focus: ['APAC market entry', 'Multi-language campaigns', 'Cross-border to SEA', 'PDPA compliant tracking'],
    stats: [
      { icon: MapPin, label: 'SEA Markets', value: '6' },
      { icon: Clock, label: 'Time Zone', value: 'SGT' },
      { icon: TrendingUp, label: 'Avg. Growth', value: '4.2x' },
      { icon: Users, label: 'SG Clients', value: '10+' },
    ],
  },
  india: {
    intro: 'Ransen is an AI-powered digital marketing agency for India, serving Mumbai, Bangalore, Delhi, Hyderabad, Chennai, and Pune. Multi-language creative, global-from-day-one attribution, and startup-friendly pricing.',
    focus: ['Multi-language creative (Hindi, Tamil, Telugu, Marathi)', 'Tier-1 + Tier-2 city strategy', 'SaaS + DTC playbooks', 'GST-compliant reporting'],
    stats: [
      { icon: MapPin, label: 'Cities', value: '18+' },
      { icon: Clock, label: 'Time Zone', value: 'IST' },
      { icon: TrendingUp, label: 'Avg. Growth', value: '4.8x' },
      { icon: Users, label: 'IN Clients', value: '25+' },
    ],
  },
  germany: {
    intro: 'Ransen is a Germany digital marketing agency serving Berlin, Munich, Frankfurt, and the wider DACH region. DSGVO-native programs with native German creative and BaFin regulatory awareness.',
    focus: ['DSGVO (German GDPR) strict compliance', 'DACH expansion (Germany + Austria + Switzerland)', 'Native German creative and copywriting', 'BaFin financial services regulatory awareness'],
    stats: [
      { icon: MapPin, label: 'German Cities', value: '6+' },
      { icon: Clock, label: 'Reporting', value: 'CET' },
      { icon: TrendingUp, label: 'Avg. Growth', value: '3.6x' },
      { icon: Users, label: 'DE Clients', value: '12+' },
    ],
  },
  france: {
    intro: 'Ransen is a France digital marketing agency serving Paris, Lyon, and the wider French market with RGPD-native programs, native French creative, and Franco-European expansion capability.',
    focus: ['RGPD + CNIL strict compliance', 'Native French creative for luxury standards', 'Paris-based reporting hours', 'ACPR financial services regulatory awareness'],
    stats: [
      { icon: MapPin, label: 'French Cities', value: '4+' },
      { icon: Clock, label: 'Reporting', value: 'CET' },
      { icon: TrendingUp, label: 'Median Lift', value: '3.4x' },
      { icon: Users, label: 'FR Clients', value: '10+' },
    ],
  },
  netherlands: {
    intro: 'Ransen is a Netherlands digital marketing agency headquartered in Amsterdam \u2014 serving EMEA HQs, Dutch fintechs, and pan-European brands with AVG-compliant programs.',
    focus: ['AVG (Dutch GDPR) compliance', 'EMEA HQ regional program management', 'Multi-language European expansion', 'DNB financial services regulatory awareness'],
    stats: [
      { icon: MapPin, label: 'NL Cities', value: '3+' },
      { icon: Clock, label: 'Reporting', value: 'CET' },
      { icon: TrendingUp, label: 'Avg. Lift', value: '3.5x' },
      { icon: Users, label: 'NL Clients', value: '9+' },
    ],
  },
  spain: {
    intro: 'Ransen is a Spain digital marketing agency serving Madrid, Barcelona, and the wider Iberian market with LOPDGDD-compliant programs, native Spanish creative, and Spain-to-LatAm expansion capability.',
    focus: ['LOPDGDD (Spanish GDPR) compliance', 'Native Peninsular Spanish creative', 'Spain \u2192 LatAm expansion programs', 'Banco de Espa\u00f1a financial services awareness'],
    stats: [
      { icon: MapPin, label: 'Spanish Cities', value: '4+' },
      { icon: Clock, label: 'Reporting', value: 'CET' },
      { icon: TrendingUp, label: 'Avg. Growth', value: '3.7x' },
      { icon: Users, label: 'ES Clients', value: '8+' },
    ],
  },
  'new-zealand': {
    intro: 'Ransen is a New Zealand digital marketing agency serving Auckland, Wellington, and Christchurch with Privacy Act 2020-compliant programs and cross-Tasman AU expansion capability.',
    focus: ['Privacy Act 2020 compliance', 'Cross-Tasman (NZ + AU) expansion', 'NZST reporting hours', 'Tourism seasonality-aware campaigns'],
    stats: [
      { icon: MapPin, label: 'NZ Cities', value: '3+' },
      { icon: Clock, label: 'Reporting', value: 'NZST' },
      { icon: TrendingUp, label: 'Avg. Growth', value: '3.9x' },
      { icon: Users, label: 'NZ Clients', value: '6+' },
    ],
  },
  'saudi-arabia': {
    intro: 'Ransen is a Saudi Arabia digital marketing agency serving Riyadh, Jeddah, NEOM, and the wider KSA market. Arabic-first creative aligned to Vision 2030 with SAMA + CMA regulatory awareness.',
    focus: ['Vision 2030 opportunity mapping', 'Native Saudi Arabic creative', 'CMA + SAMA regulatory awareness', 'Multi-city KSA expansion capability'],
    stats: [
      { icon: MapPin, label: 'KSA Cities', value: '5+' },
      { icon: Clock, label: 'Reporting', value: 'AST' },
      { icon: TrendingUp, label: 'Avg. Lift', value: '4.4x' },
      { icon: Users, label: 'KSA Clients', value: '8+' },
    ],
  },
  qatar: {
    intro: 'Ransen is a Qatar digital marketing agency serving Doha and the wider Qatari market with bilingual Arabic-English programs, QFC awareness, and post-World Cup infrastructure marketing expertise.',
    focus: ['QFC (Qatar Financial Centre) awareness', 'Bilingual Arabic-English creative', 'Post-World Cup tourism programs', 'Ramadan seasonality-aware campaigns'],
    stats: [
      { icon: MapPin, label: 'QA Cities', value: '2+' },
      { icon: Clock, label: 'Reporting', value: 'AST' },
      { icon: TrendingUp, label: 'Avg. Lift', value: '3.6x' },
      { icon: Users, label: 'QA Clients', value: '5+' },
    ],
  },
  malaysia: {
    intro: 'Ransen is a Malaysia digital marketing agency serving Kuala Lumpur, Penang, and Johor Bahru with multi-language programs (Bahasa, Mandarin, Tamil, English) and Southeast Asian expansion capability.',
    focus: ['Multi-language (Bahasa, Mandarin, Tamil, English)', 'Malaysia \u2192 SEA regional expansion', 'BNM financial services regulatory awareness', 'Halal-economy sensitivity and creative'],
    stats: [
      { icon: MapPin, label: 'MY Cities', value: '3+' },
      { icon: Clock, label: 'Reporting', value: 'MYT' },
      { icon: TrendingUp, label: 'Avg. Growth', value: '3.8x' },
      { icon: Users, label: 'MY Clients', value: '7+' },
    ],
  },
};

function useJsonLd(id, data) {
  useEffect(() => {
    if (!data) return;
    const existing = document.getElementById(id);
    if (existing) existing.remove();
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.id = id;
    script.textContent = JSON.stringify(data);
    document.head.appendChild(script);
    return () => {
      const el = document.getElementById(id);
      if (el) el.remove();
    };
  }, [id, data]);
}

export default function MarketDetail() {
  const { slug } = useParams();
  const market = MARKETS.find((m) => m.slug === slug);
  const copy = MARKET_COPY[slug];
  const canonical = typeof window !== 'undefined' ? `${window.location.origin}/markets/${slug}` : undefined;

  useSEO({
    title: market ? `Marketing Agency in ${market.name} | Ransen` : undefined,
    description: copy?.intro,
    canonical,
  });

  const countryCities = CITIES.filter((c) => c.countrySlug === slug);
  const featuredServices = SERVICES.slice(0, 6);
  const featuredIndustries = INDUSTRIES.slice(0, 6);

  const businessSchema = market ? {
    '@context': 'https://schema.org',
    '@type': 'ProfessionalService',
    name: `Ransen \u2014 ${market.name} Marketing Agency`,
    description: copy?.intro,
    url: canonical,
    areaServed: { '@type': 'Country', name: market.name },
    address: { '@type': 'PostalAddress', addressCountry: market.name },
  } : null;

  useJsonLd('market-business-schema', businessSchema);

  if (!market) {
    return (
      <div className='pt-40 pb-32 text-center'>
        <h1 className='text-3xl text-white'>Country not found</h1>
        <Link to='/locations' className='mt-6 inline-block text-brand'>Back to locations</Link>
      </div>
    );
  }

  return (
    <div>
      <PageHeader
        eyebrow={`Country \u00b7 ${market.region}`}
        title={<><span className='text-white'>Marketing Agency in </span><span className='text-brand-gradient'>{market.name}</span> <span className='text-4xl md:text-6xl'>{market.flag}</span></>}
        description={copy?.intro}
        crumbs={[{ label: 'Locations', to: '/locations' }, { label: market.name }]}
      />

      {/* Stats */}
      <section className='py-14'>
        <div className='max-w-6xl mx-auto px-6 grid sm:grid-cols-2 lg:grid-cols-4 gap-4'>
          {(copy?.stats || []).map((s, i) => {
            const I = s.icon;
            return (
              <div key={i} className='glass-card rounded-2xl p-5'>
                <div className='w-10 h-10 rounded-lg bg-gradient-to-br from-brand-dark to-brand flex items-center justify-center shadow-md shadow-brand/30'>
                  <I className='w-4 h-4 text-white' />
                </div>
                <div className='mt-4 font-heading text-3xl font-bold text-brand-gradient'>{s.value}</div>
                <div className='text-white/55 text-[13px] mt-0.5'>{s.label}</div>
              </div>
            );
          })}
        </div>
      </section>

      {/* Services offered in this country - SEO-friendly section */}
      <section className='py-16 border-t border-brand/10'>
        <div className='max-w-6xl mx-auto px-6'>
          <div className='eyebrow-pill'>Services</div>
          <h2 className='mt-5 font-heading text-3xl md:text-4xl font-bold text-white'>Digital marketing services in {market.name}</h2>
          <p className='mt-4 text-white/65 max-w-2xl font-body'>Full-service performance marketing for {market.name} brands \u2014 from paid media and SEO to AI automation and analytics.</p>
          <div className='mt-8 grid sm:grid-cols-2 lg:grid-cols-3 gap-4'>
            {featuredServices.map((s) => (
              <Link key={s.slug} to={`/services/${s.slug}`} className='glass-card rounded-2xl p-6 group'>
                <div className='w-12 h-12 bg-gradient-to-br from-brand-dark to-brand rounded-2xl flex items-center justify-center shadow-md shadow-brand/30'>
                  <ServiceIcon name={s.icon} className='w-5 h-5 text-white' />
                </div>
                <h4 className='mt-4 font-heading text-lg font-bold text-white group-hover:text-brand transition-colors'>{s.title}</h4>
                <p className='mt-2 text-white/60 text-[13.5px] leading-relaxed font-body'>{s.short}</p>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Why choose Ransen - SEO-friendly local focus areas */}
      <section className='py-16 border-t border-brand/10 bg-surface'>
        <div className='max-w-4xl mx-auto px-6'>
          <div className='eyebrow-pill'>Why Ransen</div>
          <h2 className='mt-5 font-heading text-3xl md:text-4xl font-bold text-white'>Why choose Ransen as your {market.name} marketing agency</h2>
          <div className='mt-6 grid sm:grid-cols-2 gap-3'>
            {(copy?.focus || []).map((f) => (
              <div key={f} className='glass-card rounded-xl p-4 flex items-start gap-3'>
                <div className='w-6 h-6 rounded-full bg-gradient-to-br from-brand-dark to-brand flex items-center justify-center shrink-0'>
                  <Check className='w-3 h-3 text-white' />
                </div>
                <div className='text-white/85 text-[14.5px]'>{f}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Industries we serve in this country */}
      <section className='py-16 border-t border-brand/10'>
        <div className='max-w-6xl mx-auto px-6'>
          <div className='eyebrow-pill'>Industries</div>
          <h2 className='mt-5 font-heading text-3xl md:text-4xl font-bold text-white'>Industries we serve in {market.name}</h2>
          <div className='mt-8 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3'>
            {featuredIndustries.map((i) => (
              <Link key={i.slug} to={`/industries/${i.slug}`} className='glass-card rounded-xl p-4 text-center group'>
                <div className='w-10 h-10 bg-brand/20 rounded-lg flex items-center justify-center mx-auto mb-2 group-hover:bg-brand/30 transition-colors'>
                  <ServiceIcon name={i.icon} className='w-4 h-4 text-brand' />
                </div>
                <div className='font-heading font-bold text-white text-xs leading-tight'>{i.title}</div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Cities in this country */}
      {countryCities.length > 0 && (
        <section className='py-16 border-t border-brand/10'>
          <div className='max-w-6xl mx-auto px-6'>
            <div className='eyebrow-pill'>Cities</div>
            <h2 className='mt-5 font-heading text-3xl md:text-4xl font-bold text-white'>Marketing agency locations in {market.name}</h2>
            <p className='mt-4 text-white/65 max-w-2xl font-body'>Dedicated city hubs with local strategy leads and time zone coverage across {market.name}.</p>
            <div className='mt-8 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4'>
              {countryCities.map((c) => (
                <Link key={c.slug} to={`/locations/${c.slug}`} className='glass-card rounded-2xl p-5 group'>
                  <div className='flex items-center gap-2 text-brand'>
                    <MapPin className='w-4 h-4' />
                    <div className='text-[10.5px] tracking-wider uppercase font-accent'>City Hub</div>
                  </div>
                  <div className='mt-3 font-heading font-bold text-white text-[16px]'>{c.name}</div>
                  <div className='mt-1 flex items-center gap-1.5 text-white/50 text-[12.5px]'>
                    <Users className='w-3 h-3' /> {c.population}
                  </div>
                  <div className='mt-4 inline-flex items-center gap-1.5 text-brand text-[12.5px] font-medium group-hover:gap-2.5 transition-all'>
                    Explore <ArrowRight className='w-3 h-3' />
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Final CTA - SEO-friendly */}
      <section className='py-20 border-t border-brand/10 text-center bg-gradient-to-r from-brand-dark to-brand'>
        <div className='max-w-3xl mx-auto px-6'>
          <h3 className='font-heading text-3xl md:text-4xl font-bold text-white'>Hire {market.name}\u2019s AI-powered marketing agency</h3>
          <p className='mt-4 text-white/85 font-body'>Free 30-minute strategy call with a senior Ransen strategist.</p>
          <Link to='/contact' className='mt-8 bg-white text-brand-dark hover:bg-white/90 font-semibold px-7 py-3.5 rounded-full inline-flex items-center gap-2 shadow-lg transition-colors'>
            Book a Strategy Call <ArrowRight className='w-4 h-4' />
          </Link>
        </div>
      </section>
    </div>
  );
}
